import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AnnouncementBanner,
  AnnouncementHistoryModal,
  AnnouncementModal,
} from './features/announcements';
import {
  AccountRoleBadge,
  DeviceTransferModal,
  RecoveryRecoverModal,
  SettingsModal,
  useAnonymousAuth,
  useMembershipProfileStatus,
  useMyIdentity,
  useResolvedNickname,
} from './features/auth';
import { DistrictPendingScreen, useDistrict, useDistrictState } from './features/district';
import { GisSettingsModal } from './features/map';
import { useEcwOutbox } from './features/insights';
import {
  FilterButton,
  ListPanel,
  OnlyMineToggle,
  PhotoDownloadModal,
  PostDetailModal,
  PostPanel,
  applyPostFilters,
  useAiCategoryMasters,
  useMainFilters,
  useOutbox,
  usePhotoNotice,
  usePosts,
  type PostMinimal,
} from './features/posts';
import { useApplyUrlSessionId, useMainSession } from './features/sessions';
import {
  BottomTabBar,
  Drawer,
  DrawerToggleButton,
  InstallPrompt,
  MultiTabBanner,
  useActionOutbox,
  useOnlineStatus,
} from './features/shell';
import { SurveyListModal } from './features/survey';
import { VerificationListPage } from './features/verification';
import { useToast } from '@citizen-walk/shared-ui';
import { useT } from './i18n';
import { AutoMapDialog, useColorRule } from './features/map';

/**
 * `/m/:urlToken` 配下の URL pathname に応じて 2 タブ (post / list) を出し分け。
 * map タブは `MapPage` が別 component で render し、同 BottomTabBar を共有する。
 *
 * Sprint AC — 旧 `old/apps/user/src/pages/MainPage.tsx` と同じ構成に揃える minimum 仕上げ。
 * 全画面 `h-dvh` + 簡潔ヘッダー (≡ + 地区名 + offline / 未送信 / 失敗 badge) + tab content
 * + 下部固定タブの 3 層構成。旧にない要素は撤去:
 *   - 旧 MainPage に存在しない SessionPicker inline / 地区取得 hint banner / districtState loading・error
 *     inline / nickname subtext / auth-error header badge / PostPanel 「📸 撮影前の注意」 inline button
 *   - 旧 AnnouncementHost に相当する banner + modal は v2 では `AnnouncementBanner` + `AnnouncementModal`
 *     で踏襲 (URL pattern: pattern='banner' / 'modal' で分岐)
 *
 * pathname 解決規則:
 *   - 末尾が `/post` → activeTab = 'post'
 *   - それ以外 (`/list`、index、`:sessionId` index、fallback `/`) → activeTab = 'list'
 */
function resolveActiveTab(pathname: string): 'post' | 'list' {
  if (pathname.endsWith('/post')) return 'post';
  return 'list';
}

export function AppHome() {
  const auth = useAnonymousAuth();
  const uid = auth.status === 'authenticated' ? auth.user.uid : null;
  const district = useDistrict();
  const districtId = district?.id ?? null;
  // Sprint AM: 30s timeout 後の retry 用 key (UI2 無限スピナー解消、docs/16 §10-2 P1 H4)。
  // 値を bump すると usePosts の useEffect 依存配列により subscribe が貼り直される。
  const [postsRetryKey, setPostsRetryKey] = useState(0);
  // 地区フォールバック根絶: 地区未解決のあいだ districtId は null。usePosts 等は no-op し、
  // ページ末尾の gate で DistrictPendingScreen を表示する (phase2-default 誤購読を防ぐ)。
  // R-70: 「自分」= 現 uid + 端末引き継ぎ元。docs/28: ぼかし前の写真を本人にだけ見せる判定にも使う。
  const identity = useMyIdentity(uid, districtId);
  const postsState = usePosts(uid, districtId, postsRetryKey, identity.allUids);
  const districtState = useDistrictState();
  // docs/111 Wave 3 E-2/cross-3: 投稿 authorNickname は localStorage > membership doc の順で解決する。
  const nickname = useResolvedNickname(uid, districtId);
  // R-70 方針B: 端末引き継ぎ後も「自分の投稿」(現 uid + 引き継ぎ元 linkedUids) を正しく扱う。
  // Phase E (old2 投稿フロー移植): membership.consentAcceptedAt 有無で「ユーザー設定済か」を判定。
  // 未設定 (missing) のとき PostForm が警告バナー + 投稿不可 (old2 PostTab L510-514)。
  const { incomplete: userSettingsIncomplete } = useMembershipProfileStatus(uid, districtId);
  // 案1 統合: 投稿/フィルタ/URL/お知らせの「現セッション」を membership.mainSessionId に一本化。
  const mainSession = useMainSession(uid, districtId);
  const { state: currentSessionState, setCurrentSessionId } = mainSession;
  const currentSessionId = currentSessionState.currentSessionId;
  // user 第 6 弾 (旧 `/m/:districtToken/:sessionId` 互換、L2): URL path param `:sessionId` を
  // 現セッションに自動適用。user 第 8 弾で hook 抽出 (MapPage 共通化、`/m/:urlToken/:sessionId/map`)。
  useApplyUrlSessionId(currentSessionState, currentSessionId, setCurrentSessionId);
  const location = useLocation();
  const activeTab = resolveActiveTab(location.pathname);
  // 地区フォールバック根絶: AppHome は DistrictProvider 配下 (`/m/:urlToken/...`) でのみ render される。
  // orphan PWA のデバイス引き継ぎ受領フローは DistrictHomePage (`/`) が担当するため、
  // 旧 isStandaloneAndOrphan / showConsumeForm / DeviceTransferConsumeForm は撤去 (provider 外 render 廃止に伴う)。
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [gisSettingsOpen, setGisSettingsOpen] = useState(false);
  const [photoNoticeOpen, setPhotoNoticeOpen] = useState(false);
  const photoNotice = usePhotoNotice();
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [recoverOpen, setRecoverOpen] = useState(false);
  // Sprint BG-DataSettings 8 (GAP U-C9) — デバイス間引継 modal の open state
  const [deviceTransferOpen, setDeviceTransferOpen] = useState(false);
  // Feature: 自分の投稿写真まとめてダウンロード modal の open state
  const [photoDownloadOpen, setPhotoDownloadOpen] = useState(false);
  const [detailPost, setDetailPost] = useState<PostMinimal | null>(null);
  // Phase F (old2 FilterContext 逐語移植): keyword / sessionIds[] / minAgreeCount / hasPhoto /
  // onlyMine / postFieldFilters / userFieldFilters / 現セッションのみ / 日付 / カテゴリ を 1 hook に集約。
  const filters = useMainFilters();
  // FilterPanel (全画面 bottom-sheet) の開閉。header の 🔍 button と一覧 toolbar から共有。
  const [filterOpen, setFilterOpen] = useState(false);
  const categoryMasters = useAiCategoryMasters(district?.aiAnalysisSettingsId);
  // Phase F: FilterPanel「マーカーの見方」凡例 + 一覧バッジ色用 (地区 ColorRule)。
  const colorRule = useColorRule();
  // FilterPanel の session chip / 一覧グループ名解決用 (現地区の active session 一覧)。
  const sessionOptions = useMemo(
    () => currentSessionState.items.map((s) => ({ id: s.id, name: s.name })),
    [currentSessionState.items],
  );
  const {
    state: outboxState,
    flush: flushOutbox,
    retryOne: retryOneOutbox,
    refresh: refreshOutbox,
  } = useOutbox();
  // POC: 気づき / 処方箋 の outbox 件数 header badge (旧 v1 は Firestore 永続キャッシュ
  // のみで暗黙保留していたが、v2 では明示的キュー + バッジを提供)
  const { state: ecwOutboxState } = useEcwOutbox();
  // docs/03 §7.5-3 層1: 賛同等の耐久冪等 action outbox を mount (online 時 / 復帰時に自動 flush)。
  useActionOutbox();
  // POC: 直近 flush 結果を監視し、failed > 0 のとき toast 通知 (Task B、宣言的)。
  // lastFlushResult はそのまま deps に入れると更新ごとに再 fire するが、id が新規 object なので
  // 失敗時に毎回通知される (狙い通り、auto-flush の都度ユーザーに気付かせる)。
  const lastEcwFlush = ecwOutboxState.lastFlushResult;
  // user 第 12 弾 (GAP-U5): online/offline 自動切替 UI。
  const isOnline = useOnlineStatus();
  // Phase F: header の 🔍 button badge 用 active filter 段数 (old2 FilterButton)。
  // ListPanel 内でも同値を再計算するが、header badge は posts 取得前 (0) でも整合するよう独立計算。
  const activeFilterCount = useMemo(() => {
    const all = postsState.status === 'ready' ? postsState.posts : [];
    return applyPostFilters(all, filters.toFilterState(currentSessionId, uid, identity.allUids))
      .activeFilterCount;
  }, [postsState, filters, currentSessionId, uid, identity.allUids]);
  const { toast } = useToast();
  // Sprint BG-DataSettings 19 (U-P12): header badge を i18n 化。
  const t = useT();

  // resolveDistrictByToken 結果を Toast (ready/error のみ、cache 由来は無音)。
  // 地区フォールバック根絶後は未解決中ページ末尾の gate (DistrictPendingScreen) が全画面表示を担うため、
  // 本 Toast は解決完了/失敗の補助通知 (gate を抜けた ready 後の remote 取得成功も知らせる)。
  useEffect(() => {
    if (!districtState) return;
    if (districtState.status === 'ready' && districtState.source === 'remote') {
      toast('success', `地区情報を取得: ${districtState.district.districtName}`);
    } else if (districtState.status === 'error') {
      toast('error', `地区情報の取得に失敗: ${districtState.error.message}`);
    }
  }, [districtState, toast]);

  // Task B: ecw outbox の flush で failed > 0 なら toast 通知 (POC、失敗のみ)。
  // 成功 (sent > 0) は地味で良いので一旦実装しない (header badge の減少で十分視認可能)。
  useEffect(() => {
    if (lastEcwFlush && lastEcwFlush.failed > 0) {
      toast('error', `気づき/処方箋 ${lastEcwFlush.failed} 件の送信に失敗しました`);
    }
  }, [lastEcwFlush, toast]);

  // issue #14: 初めて投稿する際は「撮影前の注意」を全面モーダルで自動表示する。
  // 未了承 (photoNotice.acknowledged=false) で投稿タブを開いた瞬間に open。old2 は写真タップ時の
  // ゲート方式だったが、本 issue の要望どおり投稿タブ表示時の自動表示に変更 (文言/レイアウトは
  // 既存 PhotoNoticeModal = old2 legal.json 逐語を再利用)。ref で 1 マウント 1 回に限定し、タブ往復で
  // 連続表示しない。キャンセル (未了承で閉じる) しても写真欄の「注意を確認する」リンクで手動再開可能。
  const photoNoticeAutoShownRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'post') return;
    if (photoNotice.acknowledged) return;
    if (photoNoticeAutoShownRef.current) return;
    photoNoticeAutoShownRef.current = true;
    setPhotoNoticeOpen(true);
  }, [activeTab, photoNotice.acknowledged]);

  // old2 入口フロー移植 (docs/105 §9): 初回参加判定は localStorage gate ではなく
  // `/m/:urlToken` の EntryPage (Firestore membership) に一本化。AppHome は onboarding 済前提で render。

  // 地区フォールバック根絶: 地区が未解決 (loading/error) の間は phase2-default を使わず、
  // 解決待ちプレースホルダを全画面表示する。以降 district は非 null に narrowing される。
  if (!district) {
    return <DistrictPendingScreen state={districtState} />;
  }

  return (
    <div className="flex flex-col h-dvh bg-white">
      {/* 旧 MainPage L132-165 踏襲 — header は ≡ Drawer + 地区名 + indicators のみ。 */}
      <header className="bg-white border-b border-slate-200 px-3 py-2 flex items-center gap-2 shrink-0 z-20">
        <DrawerToggleButton onClick={() => setDrawerOpen(true)} />
        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          {/* 地区解決済 (gate 通過後) のみ render されるため、地区名を即時表示。 */}
          <h1 className="font-medium text-sm truncate text-slate-900">{district.name}</h1>
          {/* 地区「強い停止」Phase 1: アカウント (master/担当地区管理者) ログイン中のみ表示。匿名は非表示。 */}
          <AccountRoleBadge />
        </div>
        {/* 案1: 複数セッション参加時のみ、メインセッション選択を「自分のみ」の左に表示。
            選択は membership.mainSessionId を更新し、投稿の sessionId 供給源になる。 */}
        {mainSession.needsMainSelection && (
          <select
            value={currentSessionId ?? ''}
            onChange={(e) => setCurrentSessionId(e.target.value || null)}
            aria-label="メインセッションを選択"
            title="投稿に使うメインセッション"
            className="max-w-[40vw] min-h-touch text-xs px-1.5 py-1 border border-slate-300 rounded bg-white text-slate-700 truncate"
          >
            {mainSession.joinedActiveSessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}
        {/* Phase F (old2 MainPage L144-145 逐語): 自分のみ toggle + 🔍 フィルタ button (badge)。 */}
        <OnlyMineToggle checked={filters.onlyMine} onChange={filters.setOnlyMine} />
        <FilterButton activeFilterCount={activeFilterCount} onClick={() => setFilterOpen(true)} />
        {!isOnline && (
          <span
            className="text-[10px] bg-warning-500 text-white px-1.5 py-0.5 rounded whitespace-nowrap"
            title="オフライン中"
          >
            {t('app_home.offline_badge')}
          </span>
        )}
        {outboxState.count > 0 && (
          <span
            className="text-[10px] bg-warning-500 text-white px-1.5 py-0.5 rounded whitespace-nowrap"
            title="未送信の投稿があります"
          >
            ⏳ {t('app_home.outbox_badge', { count: outboxState.count })}
          </span>
        )}
        {ecwOutboxState.count > 0 && (
          <span
            className="text-[10px] bg-warning-500 text-white px-1.5 py-0.5 rounded whitespace-nowrap"
            title="未送信の気づき/処方箋があります"
          >
            ⏳ {t('app_home.ecw_outbox_badge', { count: ecwOutboxState.count })}
          </span>
        )}
      </header>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isAuthenticated={auth.status === 'authenticated'}
        onShowAnnouncements={() => setHistoryOpen(true)}
        onShowSurveys={() => setSurveyOpen(true)}
        onShowVerification={() => setVerificationOpen(true)}
        onShowRecover={() => setRecoverOpen(true)}
        onShowSettings={() => setSettingsOpen(true)}
        onShowDeviceTransfer={() => setDeviceTransferOpen(true)}
        onShowGisSettings={() => setGisSettingsOpen(true)}
        onShowPhotoDownload={() => setPhotoDownloadOpen(true)}
      />

      {/* docs/22 GIS 設定 (要件5): gisEnabled 地区のみ Drawer に項目が出る。auth 不要。 */}
      <GisSettingsModal
        open={gisSettingsOpen}
        onClose={() => setGisSettingsOpen(false)}
        districtId={district.id}
        enabled={district.gisEnabled === true}
        districtName={district.name}
      />

      {/* Feature: 自分の投稿写真まとめてダウンロード (server 生成 ZIP の signed URL を取得)。 */}
      <PhotoDownloadModal
        open={photoDownloadOpen}
        onClose={() => setPhotoDownloadOpen(false)}
        districtId={district.id}
        uid={uid}
      />

      {/* メインコンテンツ — 旧 MainPage L167-172 踏襲 (AnnouncementHost overlay + TabPanel)。
          map タブは MapPage が別 component で render するため、本 component は post / list のみ担当。 */}
      <main className="relative flex-1 overflow-auto pb-16">
        <MultiTabBanner />
        <AnnouncementBanner districtId={district.id} currentSessionId={currentSessionId} />
        <AnnouncementModal districtId={district.id} currentSessionId={currentSessionId} />

        {activeTab === 'post' && (
          <PostPanel
            uid={uid}
            nickname={nickname}
            photoNoticeAcknowledged={photoNotice.acknowledged}
            onAcknowledgePhotoNotice={photoNotice.acknowledge}
            photoNoticeOpen={photoNoticeOpen}
            onPhotoNoticeOpen={setPhotoNoticeOpen}
            outboxState={outboxState}
            flushOutbox={flushOutbox}
            retryOneOutbox={retryOneOutbox}
            refreshOutbox={refreshOutbox}
            currentSessionId={currentSessionId}
            district={district}
            userSettingsIncomplete={userSettingsIncomplete}
            currentSessionState={currentSessionState}
            onSelectSession={setCurrentSessionId}
          />
        )}

        {activeTab === 'list' && (
          <ListPanel
            postsState={postsState}
            categoryMasters={categoryMasters}
            filters={filters}
            filterOpen={filterOpen}
            onFilterOpenChange={setFilterOpen}
            currentSessionId={currentSessionId}
            sessions={sessionOptions}
            mode={district.mode}
            postFields={district.dataSettings?.postFields ?? []}
            userFields={district.dataSettings?.userFields ?? []}
            colorRule={colorRule}
            onDetailPost={setDetailPost}
            currentUid={uid}
            selfUids={identity.allUids}
            districtId={district.id}
            onRetry={() => setPostsRetryKey((k) => k + 1)}
          />
        )}
      </main>

      {/* Drawer trigger 経由で開く Modal 群 (旧 toolbar の各 button 起動先) */}
      <AnnouncementHistoryModal
        districtId={district.id}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
      <SurveyListModal open={surveyOpen} onClose={() => setSurveyOpen(false)} />
      {auth.status === 'authenticated' && (
        <VerificationListPage
          open={verificationOpen}
          onClose={() => setVerificationOpen(false)}
          districtId={district.id}
          districtName={district.name}
          uid={auth.user.uid}
          currentSessionId={currentSessionId}
        />
      )}
      {auth.status === 'authenticated' && (
        <>
          <RecoveryRecoverModal
            open={recoverOpen}
            onClose={() => setRecoverOpen(false)}
            districtName={district.name}
            districtToken={district.urlToken}
          />
          <SettingsModal
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            districtName={district.name}
            uid={auth.user.uid}
            districtToken={district.urlToken}
          />
          <DeviceTransferModal
            open={deviceTransferOpen}
            onClose={() => setDeviceTransferOpen(false)}
          />
        </>
      )}

      {/* PostDetailModal は activeTab に関係なく表示 (list タブからのみ trigger だが、tab 切替時に
          modal を強制 unmount するのは UX 上不要なため共通 shell に置く)。 */}
      <PostDetailModal
        open={detailPost !== null}
        onClose={() => setDetailPost(null)}
        post={detailPost}
        districtId={district.id}
        currentUid={uid}
        selfUids={identity.allUids}
        onDeleted={() => setDetailPost(null)}
        analysisDisplayConfigId={district.analysisDisplayConfigId}
      />

      {/* user 第 14 弾 (GAP-U10): PWA インストール促進。standalone 起動済なら自動非表示。 */}
      <InstallPrompt />

      {/* Sprint BG-DataSettings 19 (U-C13): district.mapDownloadPolicy === 'auto' のときに
          初回起動で AutoMapDialog を表示 (旧 v1 components/AutoMapDialog.tsx 159 行踏襲)。 */}
      <AutoMapDialog districtId={district.id} districtName={district.name} />

      <BottomTabBar urlToken={district.urlToken} />
    </div>
  );
}
